/**
 * TechListDialog — Technology Database showing all technologies in a
 * categorized list view (not a graph). Left sidebar has category filters,
 * main area shows a scrollable tier-grouped list, and a right detail panel
 * shows full info for the selected tech.
 */

import Phaser from 'phaser';
import { Colors, CSSColors, Fonts, FontSize, Radius, createDialogChrome, closeDialog } from './GameUITheme';
import { I18n } from '../../i18n/I18nService';
import { isMobile } from '../../utils/ResponsiveHelper';

const PAD = 8;
const CONTENT_TOP = 58;

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('crimsonmandate_token');
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// ── Data types ──────────────────────────────────────────────────

type TechCategory = 'ALL' | 'WEAPONS' | 'ENGINES' | 'SHIELDS' | 'SENSORS' | 'SHIPS';
type TechStatus = 'UNLOCKED' | 'RESEARCHING' | 'RELIC_FOUND' | 'LOCKED' | 'UNDISCOVERED';

interface TechPrerequisite {
  techId: string;
  techName: string;
  status: TechStatus;
}

interface MineralCost {
  mineralName: string;
  amount: number;
}

interface Technology {
  id: string;
  name: string;
  tier: number;
  category: string;
  status: TechStatus;
  description: string;
  unlocksLabel: string;
  prerequisites: TechPrerequisite[];
  isdCost: number;
  mineralCosts: MineralCost[];
  canResearchAtEarth: boolean;
  canResearchAtStation: boolean;
}

// Tier display info
const TIER_INFO: Record<number, { hex: number; css: string; label: string }> = {
  1: { hex: 0x888888, css: '#aaaaaa', label: 'TIER 1 \u2014 BASIC' },
  2: { hex: 0x4488ff, css: '#4488ff', label: 'TIER 2 \u2014 ADVANCED' },
  3: { hex: 0xffcc00, css: '#ffcc00', label: 'TIER 3 \u2014 SUPERIOR' },
  4: { hex: 0xcc66ff, css: '#cc66ff', label: 'TIER 4 \u2014 EPIC' },
  5: { hex: 0xff8800, css: '#ff8800', label: 'TIER 5 \u2014 LEGENDARY' },
};

// Status display config
const STATUS_CONFIG: Record<TechStatus, { symbol: string; color: string; label: string }> = {
  UNLOCKED:     { symbol: '\u2713', color: '#00ffff', label: 'UNLOCKED' },
  RESEARCHING:  { symbol: '\u2022\u2022\u2022', color: '#ffcc00', label: 'RESEARCHING...' },
  RELIC_FOUND:  { symbol: '\u25CF', color: '#4488ff', label: 'RELIC AVAILABLE' },
  LOCKED:       { symbol: '\uD83D\uDD12', color: '#556677', label: 'LOCKED' },
  UNDISCOVERED: { symbol: '?', color: '#334455', label: '???' },
};

const CATEGORIES: TechCategory[] = ['ALL', 'WEAPONS', 'ENGINES', 'SHIELDS', 'SENSORS', 'SHIPS'];

export class TechListDialog extends Phaser.GameObjects.Container {
  private activeCategory: TechCategory = 'ALL';
  private selectedTechId: string | null = null;
  private technologies: Technology[] = [];

  // UI containers
  private sidebarContainer: Phaser.GameObjects.Container;
  private mainContainer: Phaser.GameObjects.Container;
  private detailContainer: Phaser.GameObjects.Container;
  private mainScrollMask!: Phaser.Display.Masks.GeometryMask;
  private statusText: Phaser.GameObjects.Text;

  // Animated dots timer for RESEARCHING status
  private dotTimer: Phaser.Time.TimerEvent | null = null;
  private dotFrame = 0;

  // Optional station context
  private stationId?: string;

  // Responsive layout
  private mobile: boolean;
  private sidebarW: number;
  private mainX: number;
  private mainW: number;
  private detailW: number;
  private contentH: number;

  constructor(scene: Phaser.Scene, stationId?: string) {
    const mobile = isMobile(scene);
    const dw = mobile ? 340 : 800;
    const dh = mobile ? 540 : 620;
    const { width, height } = scene.cameras.main;
    super(scene, width / 2 - dw / 2, height / 2 - dh / 2);
    this.mobile = mobile;
    this.contentH = dh - CONTENT_TOP - 24;

    // Layout columns
    const SIDEBAR_W = 120;
    const SIDEBAR_X = PAD;
    const MAIN_X = SIDEBAR_X + SIDEBAR_W + 4;
    const MAIN_W = dw - MAIN_X - PAD;
    // Detail panel overlaps the main list, starting at the left edge of the dialog
    const DETAIL_X = PAD;
    const DETAIL_W = dw - PAD * 2;

    this.sidebarW = SIDEBAR_W;
    this.mainX = MAIN_X;
    this.mainW = MAIN_W;
    this.detailW = DETAIL_W;

    this.stationId = stationId;
    const title = stationId ? 'Station Technologies' : I18n.t('dialog.tech_database');

    createDialogChrome(scene, this, dw, dh, {
      title,
      onClose: () => this.close(),
    });

    // Main scrollable list area (added first so sidebar renders on top)
    this.mainContainer = scene.add.container(MAIN_X, CONTENT_TOP);
    this.add(this.mainContainer);
    this.setupMainScrollMask();

    // Left sidebar (category tabs) — rendered after main so it's on top
    this.sidebarContainer = scene.add.container(SIDEBAR_X, CONTENT_TOP);
    this.add(this.sidebarContainer);

    // Detail panel (on top of everything when visible)
    this.detailContainer = scene.add.container(DETAIL_X, CONTENT_TOP);
    this.add(this.detailContainer);

    // Hide detail panel until a tech is selected (overlaps main list)
    this.detailContainer.setVisible(false);
    if (mobile) {
      this.mainContainer.setVisible(false);
    }

    // Status text (bottom)
    this.statusText = scene.add.text(dw / 2, dh - 12, '', {
      fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.body,
    });
    this.statusText.setOrigin(0.5);
    this.add(this.statusText);

    // Animated dots timer for researching techs
    this.dotTimer = scene.time.addEvent({
      delay: 500,
      loop: true,
      callback: () => {
        this.dotFrame = (this.dotFrame + 1) % 4;
      },
    });

    this.setDepth(2000);
    scene.add.existing(this);

    this.loadData();

    // Cleanup on destroy
    this.once('destroy', () => {
      if (this.dotTimer) {
        this.dotTimer.destroy();
        this.dotTimer = null;
      }
    });
  }

  private close(): void {
    if (this.dotTimer) {
      this.dotTimer.destroy();
      this.dotTimer = null;
    }
    closeDialog(this.scene, this);
  }

  private setupMainScrollMask(): void {
    const maskGraphics = this.scene.add.graphics();
    maskGraphics.fillStyle(0xffffff);
    maskGraphics.fillRect(this.x + this.mainX, this.y + CONTENT_TOP, this.mainW, this.contentH);
    maskGraphics.setVisible(false);
    this.mainScrollMask = new Phaser.Display.Masks.GeometryMask(this.scene, maskGraphics);
    this.mainContainer.setMask(this.mainScrollMask);

    this.scene.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gos: unknown, _dx: number, dy: number) => {
      const contentH = this.mainContainer.getBounds().height;
      if (contentH <= this.contentH) return;
      const maxScroll = -(contentH - this.contentH);
      const localY = this.mainContainer.y - CONTENT_TOP;
      const newLocalY = Phaser.Math.Clamp(localY - dy * 0.5, maxScroll, 0);
      this.mainContainer.y = CONTENT_TOP + newLocalY;
    });
  }

  // ============ Data Loading ============

  private async loadData(): Promise<void> {
    this.statusText.setText('Loading...');

    try {
      const url = this.stationId
        ? `/api/research/tech-tree?stationId=${encodeURIComponent(this.stationId)}`
        : '/api/research/tech-tree';
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw: any = await res.json();
      const list: any[] = Array.isArray(raw) ? raw : (raw.technologies || []);
      // Transform server response to client Technology shape
      this.technologies = list.map((t: any) => ({
        id: String(t.id),
        name: t.name,
        tier: t.tier,
        category: t.category,
        status: t.isUnlocked ? 'UNLOCKED' as TechStatus : 'LOCKED' as TechStatus,
        description: t.description || '',
        unlocksLabel: t.unlocksLabel || '',
        prerequisites: Array.isArray(t.prerequisites)
          ? t.prerequisites.map((p: any) => typeof p === 'object'
            ? { techId: String(p.id || p.techId), techName: p.name || p.techName || '???', status: (p.isUnlocked ? 'UNLOCKED' : 'LOCKED') as TechStatus }
            : { techId: String(p), techName: '???', status: 'LOCKED' as TechStatus })
          : [],
        isdCost: t.isdCost || 0,
        mineralCosts: t.mineralCosts
          ? (Array.isArray(t.mineralCosts)
            ? t.mineralCosts
            : Object.entries(t.mineralCosts).map(([k, v]) => ({ mineralName: k.replace('min_', ''), amount: v as number })))
          : [],
        canResearchAtEarth: true,
        canResearchAtStation: true,
      }));
    } catch {
      this.technologies = [];
    }

    this.statusText.setText(`${this.technologies.length} technolog${this.technologies.length === 1 ? 'y' : 'ies'}`);

    // Auto-select first visible tech
    const filtered = this.getFilteredTechs();
    if (filtered.length > 0 && !this.selectedTechId) {
      this.selectedTechId = filtered[0].id;
    }

    this.renderSidebar();
    this.renderMainList();
    this.renderDetail();
  }

  private getFilteredTechs(): Technology[] {
    if (this.activeCategory === 'ALL') return this.technologies;
    return this.technologies.filter(t => t.category.toUpperCase() === this.activeCategory);
  }

  // ============ Sidebar (Category Tabs) ============

  private renderSidebar(): void {
    this.sidebarContainer.removeAll(true);

    // Panel background
    const sidebarBg = this.scene.add.graphics();
    sidebarBg.fillStyle(Colors.bgCard, 0.6);
    sidebarBg.fillRoundedRect(0, 0, this.sidebarW, this.contentH, Radius.sm);
    sidebarBg.lineStyle(1, Colors.borderDim, 0.15);
    sidebarBg.strokeRoundedRect(0, 0, this.sidebarW, this.contentH, Radius.sm);
    this.sidebarContainer.add(sidebarBg);

    let y = 8;
    for (const cat of CATEGORIES) {
      const isActive = this.activeCategory === cat;
      const tabH = 32;

      const tabContainer = this.scene.add.container(0, y);

      const tabBg = this.scene.add.graphics();
      const drawTabBg = (active: boolean, hover: boolean) => {
        tabBg.clear();
        const fill = active ? 0x253a66 : hover ? 0x1e2e55 : 0x00000000;
        tabBg.fillStyle(fill, active || hover ? 0.9 : 0);
        tabBg.fillRoundedRect(4, 0, this.sidebarW - 8, tabH, Radius.sm);

        // Active: cyan left border accent
        if (active) {
          tabBg.fillStyle(Colors.cyan, 0.8);
          tabBg.fillRect(4, 4, 3, tabH - 8);
        }
      };
      drawTabBg(isActive, false);
      tabContainer.add(tabBg);

      const tabText = this.scene.add.text(20, tabH / 2, cat, {
        fontSize: FontSize.sm,
        color: isActive ? CSSColors.textWhite : CSSColors.textMuted,
        fontFamily: Fonts.heading,
      });
      tabText.setOrigin(0, 0.5);
      tabContainer.add(tabText);

      // Count badge
      const count = cat === 'ALL'
        ? this.technologies.length
        : this.technologies.filter(t => t.category.toUpperCase() === cat).length;
      const countText = this.scene.add.text(this.sidebarW - 16, tabH / 2, String(count), {
        fontSize: '10px', color: isActive ? CSSColors.cyan : CSSColors.textDim, fontFamily: Fonts.body,
      });
      countText.setOrigin(1, 0.5);
      tabContainer.add(countText);

      // Hit zone
      const hitZone = this.scene.add.zone(4, 0, this.sidebarW - 8, tabH).setOrigin(0, 0);
      hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.sidebarW - 8, tabH), Phaser.Geom.Rectangle.Contains);
      hitZone.on('pointerover', () => {
        if (!isActive) drawTabBg(false, true);
        tabText.setColor(isActive ? CSSColors.textWhite : CSSColors.textLight);
        this.scene.input.setDefaultCursor('pointer');
      });
      hitZone.on('pointerout', () => {
        drawTabBg(isActive, false);
        tabText.setColor(isActive ? CSSColors.textWhite : CSSColors.textMuted);
        this.scene.input.setDefaultCursor('default');
      });
      hitZone.on('pointerdown', () => {
        this.activeCategory = cat;
        this.mainContainer.y = CONTENT_TOP; // reset scroll
        this.renderSidebar();
        this.renderMainList();
        // Update selection if current selection is filtered out
        const filtered = this.getFilteredTechs();
        if (filtered.length > 0 && (!this.selectedTechId || !filtered.find(t => t.id === this.selectedTechId))) {
          this.selectedTechId = filtered[0].id;
        }
        this.renderDetail();
        // Mobile: selecting a category shows the main list
        if (this.mobile) {
          this.sidebarContainer.setVisible(false);
          this.mainContainer.setVisible(true);
          this.detailContainer.setVisible(false);
        }
      });
      tabContainer.add(hitZone);

      this.sidebarContainer.add(tabContainer);
      y += tabH + 4;
    }
  }

  // ============ Main List ============

  private renderMainList(): void {
    this.mainContainer.removeAll(true);
    let yOffset = 0;

    // Mobile: add back button to go back to sidebar (categories)
    if (this.mobile) {
      const backBtn = this.createMobileBackBtn(() => {
        this.sidebarContainer.setVisible(true);
        this.mainContainer.setVisible(false);
        this.detailContainer.setVisible(false);
      });
      this.mainContainer.add(backBtn);
      yOffset = 30;
    }

    const techs = this.getFilteredTechs();

    if (techs.length === 0) {
      const empty = this.scene.add.text(this.mainW / 2, 40 + yOffset, 'No technologies found.', {
        fontSize: FontSize.md, color: '#556677', fontFamily: Fonts.body,
      });
      empty.setOrigin(0.5, 0);
      this.mainContainer.add(empty);
      return;
    }

    // Group by tier
    const tiers = new Map<number, Technology[]>();
    for (const tech of techs) {
      const existing = tiers.get(tech.tier) || [];
      existing.push(tech);
      tiers.set(tech.tier, existing);
    }

    // Sort tier keys
    const sortedTiers = Array.from(tiers.keys()).sort((a, b) => a - b);

    let y = yOffset;
    for (const tier of sortedTiers) {
      const tierInfo = TIER_INFO[tier] || TIER_INFO[1];
      const tierTechs = tiers.get(tier)!;

      // Tier header
      const tierHeader = this.scene.add.text(0, y, tierInfo.label, {
        fontSize: FontSize.xs, color: tierInfo.css, fontFamily: Fonts.heading,
        letterSpacing: 1,
      });
      this.mainContainer.add(tierHeader);
      y += 18;

      // Divider under header
      const divider = this.scene.add.graphics();
      divider.lineStyle(1, tierInfo.hex, 0.2);
      divider.lineBetween(0, y, this.mainW, y);
      this.mainContainer.add(divider);
      y += 6;

      for (const tech of tierTechs) {
        const row = this.createTechRow(tech, y);
        this.mainContainer.add(row);
        y += 48;
      }

      y += 8; // gap between tiers
    }
  }

  private createTechRow(tech: Technology, y: number): Phaser.GameObjects.Container {
    const row = this.scene.add.container(0, y);
    const rowH = 44;
    const isSelected = this.selectedTechId === tech.id;
    const isUndiscovered = tech.status === 'UNDISCOVERED';
    const statusConf = STATUS_CONFIG[tech.status];
    const tierInfo = TIER_INFO[tech.tier] || TIER_INFO[1];

    // Row background
    const bg = this.scene.add.graphics();
    const drawRowBg = (selected: boolean, hover: boolean) => {
      bg.clear();
      const fill = selected ? 0x253a66 : hover ? 0x1e2e55 : Colors.bgCard;
      const alpha = isUndiscovered ? 0.4 : 0.9;
      bg.fillStyle(fill, alpha);
      bg.fillRoundedRect(0, 0, this.mainW, rowH, Radius.sm);
      const bColor = selected ? Colors.cyan : hover ? Colors.cyan : Colors.borderDim;
      const bAlpha = selected ? 0.3 : hover ? 0.15 : 0.08;
      bg.lineStyle(1, bColor, bAlpha);
      bg.strokeRoundedRect(0, 0, this.mainW, rowH, Radius.sm);
    };
    drawRowBg(isSelected, false);
    row.add(bg);

    // Tier dot
    const tierDot = this.scene.add.graphics();
    tierDot.fillStyle(tierInfo.hex, isUndiscovered ? 0.3 : 1);
    tierDot.fillCircle(14, rowH / 2, 4);
    row.add(tierDot);

    // Tech name
    const displayName = isUndiscovered ? '???' : tech.name;
    const nameText = this.scene.add.text(28, 6, displayName, {
      fontSize: FontSize.sm,
      color: isUndiscovered ? CSSColors.textDim : isSelected ? CSSColors.textWhite : CSSColors.textLight,
      fontFamily: isSelected ? Fonts.heading : Fonts.body,
    });
    row.add(nameText);

    // Category label (small, muted)
    if (!isUndiscovered) {
      const catText = this.scene.add.text(28, 24, tech.category.toUpperCase(), {
        fontSize: '9px', color: CSSColors.textMuted, fontFamily: Fonts.body,
      });
      row.add(catText);
    }

    // Status indicator (right side)
    const statusSymbol = this.scene.add.text(this.mainW - 10, 7, statusConf.symbol, {
      fontSize: '11px', color: statusConf.color, fontFamily: Fonts.body,
    });
    statusSymbol.setOrigin(1, 0);
    row.add(statusSymbol);

    const statusLabel = this.scene.add.text(this.mainW - 10, 24, statusConf.label, {
      fontSize: '9px', color: statusConf.color, fontFamily: Fonts.heading,
    });
    statusLabel.setOrigin(1, 0);
    row.add(statusLabel);

    // Pulsing effect for RELIC_FOUND
    if (tech.status === 'RELIC_FOUND') {
      this.scene.tweens.add({
        targets: statusSymbol,
        alpha: { from: 1, to: 0.3 },
        duration: 1000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // Hit zone for selection
    const hitZone = this.scene.add.zone(0, 0, this.mainW, rowH).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.mainW, rowH), Phaser.Geom.Rectangle.Contains);
    hitZone.on('pointerover', () => {
      if (!isSelected) drawRowBg(false, true);
      this.scene.input.setDefaultCursor('pointer');
    });
    hitZone.on('pointerout', () => {
      drawRowBg(isSelected, false);
      this.scene.input.setDefaultCursor('default');
    });
    hitZone.on('pointerdown', () => {
      this.selectedTechId = tech.id;
      this.renderMainList();
      this.renderDetail();
      // Show detail panel overlapping the main list
      this.detailContainer.setVisible(true);
      if (this.mobile) {
        this.sidebarContainer.setVisible(false);
        this.mainContainer.setVisible(false);
      }
    });
    row.add(hitZone);

    return row;
  }

  // ============ Detail Panel (Right) ============

  private renderDetail(): void {
    this.detailContainer.removeAll(true);

    // Panel background
    const detailBg = this.scene.add.graphics();
    detailBg.fillStyle(Colors.bgCard, 0.6);
    detailBg.fillRoundedRect(0, 0, this.detailW, this.contentH, Radius.sm);
    detailBg.lineStyle(1, Colors.borderDim, 0.15);
    detailBg.strokeRoundedRect(0, 0, this.detailW, this.contentH, Radius.sm);
    this.detailContainer.add(detailBg);

    const tech = this.technologies.find(t => t.id === this.selectedTechId);
    if (!tech) {
      const hint = this.scene.add.text(this.detailW / 2, 60, 'Select a technology\nto view details', {
        fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.body, align: 'center',
      });
      hint.setOrigin(0.5, 0);
      this.detailContainer.add(hint);
      return;
    }

    const isUndiscovered = tech.status === 'UNDISCOVERED';
    const innerPad = 12;
    let y = innerPad;

    // Back button to dismiss detail panel and show the list
    const backBtn = this.createMobileBackBtn(() => {
      this.detailContainer.setVisible(false);
      if (this.mobile) {
        this.mainContainer.setVisible(true);
      }
    });
    this.detailContainer.add(backBtn);
    y += 30;

    // Tech name (xl font)
    const displayName = isUndiscovered ? '???' : tech.name;
    const nameText = this.scene.add.text(innerPad, y, displayName, {
      fontSize: FontSize.xl,
      color: isUndiscovered ? CSSColors.textDim : CSSColors.textWhite,
      fontFamily: Fonts.heading,
      wordWrap: { width: this.detailW - innerPad * 2 },
    });
    this.detailContainer.add(nameText);
    y += nameText.height + 4;

    // Tier + Category line
    const tierInfo = TIER_INFO[tech.tier] || TIER_INFO[1];
    const tierCatText = this.scene.add.text(innerPad, y, `Tier ${tech.tier} \u2022 ${tech.category}`, {
      fontSize: FontSize.xs, color: tierInfo.css, fontFamily: Fonts.heading,
    });
    this.detailContainer.add(tierCatText);
    y += 18;

    // Status badge
    const statusConf = STATUS_CONFIG[tech.status];
    const statusBadge = this.scene.add.text(innerPad, y, `${statusConf.symbol} ${statusConf.label}`, {
      fontSize: FontSize.xs, color: statusConf.color, fontFamily: Fonts.heading,
    });
    this.detailContainer.add(statusBadge);
    y += 22;

    // Divider
    this.addDetailDivider(y); y += 10;

    // Description
    if (!isUndiscovered && tech.description) {
      const desc = this.scene.add.text(innerPad, y, tech.description, {
        fontSize: FontSize.xs, color: CSSColors.textMuted, fontFamily: Fonts.body,
        wordWrap: { width: this.detailW - innerPad * 2 }, lineSpacing: 2,
      });
      this.detailContainer.add(desc);
      y += desc.height + 10;

      this.addDetailDivider(y); y += 10;
    }

    // Unlocks section
    if (!isUndiscovered && tech.unlocksLabel) {
      const unlocksHeader = this.scene.add.text(innerPad, y, 'Unlocks:', {
        fontSize: FontSize.xs, color: CSSColors.cyanDim, fontFamily: Fonts.heading,
      });
      this.detailContainer.add(unlocksHeader);
      y += 16;

      const unlocksText = this.scene.add.text(innerPad + 8, y, tech.unlocksLabel, {
        fontSize: FontSize.xs, color: CSSColors.textLight, fontFamily: Fonts.body,
        wordWrap: { width: this.detailW - innerPad * 2 - 8 },
      });
      this.detailContainer.add(unlocksText);
      y += unlocksText.height + 10;
    }

    // Prerequisites section
    if (!isUndiscovered && tech.prerequisites && tech.prerequisites.length > 0) {
      const prereqHeader = this.scene.add.text(innerPad, y, 'Prerequisites:', {
        fontSize: FontSize.xs, color: CSSColors.cyanDim, fontFamily: Fonts.heading,
      });
      this.detailContainer.add(prereqHeader);
      y += 16;

      for (const prereq of tech.prerequisites) {
        const prereqStatus = STATUS_CONFIG[prereq.status] || STATUS_CONFIG.LOCKED;
        const prereqText = this.scene.add.text(innerPad + 8, y, `${prereqStatus.symbol} ${prereq.techName}`, {
          fontSize: FontSize.xs, color: prereqStatus.color, fontFamily: Fonts.body,
        });
        this.detailContainer.add(prereqText);
        y += 16;
      }
      y += 6;
    }

    // Research cost section
    if (!isUndiscovered && (tech.isdCost > 0 || tech.mineralCosts.length > 0)) {
      this.addDetailDivider(y); y += 10;

      const costHeader = this.scene.add.text(innerPad, y, 'Research Cost:', {
        fontSize: FontSize.xs, color: CSSColors.cyanDim, fontFamily: Fonts.heading,
      });
      this.detailContainer.add(costHeader);
      y += 16;

      if (tech.isdCost > 0) {
        const isdText = this.scene.add.text(innerPad + 8, y, `$${tech.isdCost.toLocaleString()} ISD`, {
          fontSize: FontSize.xs, color: CSSColors.gold, fontFamily: Fonts.body,
        });
        this.detailContainer.add(isdText);
        y += 16;
      }

      for (const mineral of tech.mineralCosts) {
        const mineralText = this.scene.add.text(innerPad + 8, y, `${mineral.amount}x ${mineral.mineralName}`, {
          fontSize: FontSize.xs, color: CSSColors.textLight, fontFamily: Fonts.body,
        });
        this.detailContainer.add(mineralText);
        y += 16;
      }
      y += 6;
    }

    // Action buttons (only if relic is available)
    if (tech.status === 'RELIC_FOUND') {
      this.addDetailDivider(y); y += 10;

      if (tech.canResearchAtEarth) {
        const earthBtn = this.createActionBtn(innerPad, y, this.detailW - innerPad * 2, 28, 'Research at Earth', 0x1a3344, () => {
          this.initiateResearch(tech.id, 'earth');
        });
        this.detailContainer.add(earthBtn);
        y += 34;
      }

      if (tech.canResearchAtStation) {
        const stationBtn = this.createActionBtn(innerPad, y, this.detailW - innerPad * 2, 28, 'Research at Station', 0x2a1a44, () => {
          this.initiateResearch(tech.id, 'station');
        });
        this.detailContainer.add(stationBtn);
        y += 34;
      }
    }
  }

  // ============ Actions ============

  private async initiateResearch(techId: string, location: 'earth' | 'station'): Promise<void> {
    this.statusText.setText('Starting research...');

    try {
      const res = await fetch('/api/research/start', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ techId, location }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Unknown error' }));
        this.statusText.setText(err.error || 'Failed to start research');
        return;
      }
      this.statusText.setText('Research started!');
      this.loadData(); // refresh
    } catch {
      this.statusText.setText('Network error');
    }
  }

  // ============ Helpers ============

  private createMobileBackBtn(onBack: () => void): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(0, 0);
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x1e2844, 0.9);
    bg.fillRoundedRect(0, 0, 80, 26, 4);
    bg.lineStyle(1, 0x00ffff, 0.1);
    bg.strokeRoundedRect(0, 0, 80, 26, 4);
    btn.add(bg);
    btn.add(this.scene.add.text(40, 13, '\u2190 Back', {
      fontSize: '12px', color: '#8899bb', fontFamily: 'Share Tech Mono, monospace',
    }).setOrigin(0.5));
    const zone = this.scene.add.zone(0, 0, 80, 26).setOrigin(0, 0);
    zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, 80, 26), Phaser.Geom.Rectangle.Contains);
    zone.on('pointerdown', onBack);
    btn.add(zone);
    return btn;
  }

  private addDetailDivider(y: number): void {
    const g = this.scene.add.graphics();
    g.lineStyle(1, Colors.cyan, 0.08);
    g.lineBetween(8, y, this.detailW - 8, y);
    this.detailContainer.add(g);
  }

  private createActionBtn(
    x: number, y: number, w: number, h: number,
    label: string, color: number, callback: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);

    const bg = this.scene.add.graphics();
    const drawBg = (hover: boolean) => {
      bg.clear();
      bg.fillStyle(hover ? color + 0x111111 : color, 1);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, Colors.cyan, hover ? 0.3 : 0.15);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
    };
    drawBg(false);
    btn.add(bg);

    const text = this.scene.add.text(w / 2, h / 2, label, {
      fontSize: FontSize.xs, color: CSSColors.textWhite, fontFamily: Fonts.heading,
    });
    text.setOrigin(0.5);
    btn.add(text);

    const hitZone = this.scene.add.zone(0, 0, w, h).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    hitZone.on('pointerover', () => {
      drawBg(true);
      this.scene.input.setDefaultCursor('pointer');
    });
    hitZone.on('pointerout', () => {
      drawBg(false);
      this.scene.input.setDefaultCursor('default');
    });
    hitZone.on('pointerdown', callback);
    btn.add(hitZone);

    return btn;
  }
}
